import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Shield, Lock, LogIn } from "lucide-react";
import useAuth from "../../hooks/useAuth";
import { LoginDialog } from "../shared/LoginDialog";

interface AccessDeniedProps {
  reason?: "unauthorized" | "forbidden" | "not_found";
  activityName?: string;
  onRetry?: () => void;
}

const AccessDenied = ({ reason = "forbidden", activityName, onRetry }: AccessDeniedProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { auth } = useAuth();
  const [searchParams] = useSearchParams();
  const [showLoginDialog, setShowLoginDialog] = useState(false);

  const isAuthenticated = !!auth?.email;

  const getContent = () => {
    if (reason === "unauthorized" || !isAuthenticated) {
      // User needs to log in — show login modal instead of navigating away
      return {
        icon: <LogIn className="w-16 h-16 text-primary" />,
        title: t("login_required") || "Login Required",
        description:
          t("login_required_description") ||
          "This activity requires you to be logged in to access it.",
        action: {
          label: t("login") || "Sign In",
          onClick: () => {
            setShowLoginDialog(true);
          },
        },
        secondaryAction: {
          label: t("sign_up") || "Sign Up",
          onClick: () => {
            const currentPath = window.location.pathname + window.location.search;
            navigate(`/register?redirect=${encodeURIComponent(currentPath)}`);
          },
        },
      };
    }

    if (reason === "forbidden") {
      // User is logged in but doesn't have permission
      return {
        icon: <Shield className="w-16 h-16 text-orange-500" />,
        title: t("access_restricted") || "Access Restricted",
        description:
          t("access_restricted_description") ||
          "You don't have permission to access this activity. Only specific users who have been granted access can view it.",
        action: {
          label: t("go_to_dashboard") || "Go to Dashboard",
          onClick: () => navigate("/portal"),
        },
        secondaryAction: null,
        additionalInfo:
          t("contact_owner_for_access") ||
          "Please contact the activity owner if you believe you should have access.",
      };
    }

    // not_found
    return {
      icon: <Lock className="w-16 h-16 text-gray-500" />,
      title: t("activity_not_found") || "Activity Not Found",
      description:
        t("activity_not_found_description") ||
        "The activity you're trying to access doesn't exist or has been removed.",
      action: {
        label: t("go_to_dashboard") || "Go to Dashboard",
        onClick: () => navigate("/portal"),
      },
      secondaryAction: null,
    };
  };

  const content = getContent();

  const handleLoginSuccess = () => {
    setShowLoginDialog(false);
    // Retry loading the activity after successful login
    if (onRetry) {
      onRetry();
    } else {
      // Fallback: reload the page to re-attempt loading
      window.location.reload();
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-lg shadow-lg p-8 text-center">
          {/* Icon */}
          <div className="flex justify-center mb-6">{content.icon}</div>

          {/* Title */}
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            {content.title}
          </h1>

          {/* Activity Name (if available) */}
          {activityName && (
            <div className="mb-4 text-sm text-gray-600">
              <span className="font-medium">{t("activity")}:</span> {activityName}
            </div>
          )}

          {/* Description */}
          <p className="text-gray-600 mb-6">{content.description}</p>

          {/* Additional Info */}
          {content.additionalInfo && (
            <div className="bg-primary/5 border border-primary/20 rounded-md p-4 mb-6">
              <p className="text-sm text-primary">{content.additionalInfo}</p>
            </div>
          )}

          {/* Current User Info (for debugging) */}
          {isAuthenticated && (
            <div className="mb-6 text-xs text-gray-500">
              {t("logged_in_as")}: {auth.email}
            </div>
          )}

          {/* Actions */}
          <div className="space-y-3">
            <button
              onClick={content.action.onClick}
              className="w-full bg-primary text-white py-2 px-4 rounded-md hover:bg-primary/90 transition-colors font-medium"
            >
              {content.action.label}
            </button>

            {content.secondaryAction && (
              <button
                onClick={content.secondaryAction.onClick}
                className="w-full bg-white text-primary py-2 px-4 rounded-md border border-primary hover:bg-primary/5 transition-colors font-medium"
              >
                {content.secondaryAction.label}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Login Dialog — shown in-place instead of navigating to /login */}
      <LoginDialog
        open={showLoginDialog}
        onOpenChange={setShowLoginDialog}
        onLoginSuccess={handleLoginSuccess}
        title={t("login_to_continue") || "Login to continue"}
        description={
          t("login_required_to_access_activity") ||
          "Please sign in to access this activity."
        }
      />
    </div>
  );
};

export default AccessDenied;
